/**
 * DD-PEOPLE-001 — the D&D people directory (Teams-backed, loaded from the API).
 *
 * One group per step role, each the members of the RUKH Team configured for that role
 * (`config/dd_workflow.php` → `GET /v1/design-development/people`). This replaces the
 * hardcoded operator list as the source of assignable / mentionable people everywhere in
 * the D&D module; `data/operators.ts` remains fixture data for tests and the demo generator.
 *
 * The directory lives in the queue store (reactive) and is mirrored here as a plain
 * singleton so pure helpers (mention extraction, filters) can read it without importing
 * the store — same seam as `ddQueueIdentity.ts`.
 */
import type { StepRole } from '@/lib/dd-queue/types'

export interface DdPerson {
  uuid: string
  name: string
  email: string
}

/** The five team-backed roles, in the order the module renders them. */
export const DD_PEOPLE_ROLES = ['engineer', 'design', 'estimate', 'sales', 'prepress'] as const
export type DdPeopleRole = (typeof DD_PEOPLE_ROLES)[number]

export const DD_PEOPLE_ROLE_LABELS: Record<DdPeopleRole, string> = {
  engineer: 'Product Development',
  design: 'Design',
  estimate: 'Estimation',
  sales: 'Sales',
  prepress: 'Pre-press'
}

export interface DdPeopleDirectory {
  groups: Record<DdPeopleRole, DdPerson[]>
  all: DdPerson[]
  /** Resolved team name per role; null when the configured team was not found. */
  teams: Record<DdPeopleRole, string | null>
}

export interface DdPeopleGroup {
  key: DdPeopleRole
  label: string
  people: DdPerson[]
}

/** Mention targets that are groups, not people — kept as before (resolved server-side later). */
export const MENTION_GROUPS = ['Sales', 'Estimator'] as const

export function emptyPeopleDirectory(): DdPeopleDirectory {
  return {
    groups: { engineer: [], design: [], estimate: [], sales: [], prepress: [] },
    all: [],
    teams: { engineer: null, design: null, estimate: null, sales: null, prepress: null }
  }
}

function isPerson(v: unknown): v is DdPerson {
  if (!v || typeof v !== 'object') return false
  const o = v as Record<string, unknown>
  return typeof o.uuid === 'string' && o.uuid !== '' && typeof o.name === 'string' && o.name !== ''
}

/** Normalise the API payload: unknown keys dropped, malformed entries skipped, never throws. */
export function peopleDirectoryFromApi(raw: unknown): DdPeopleDirectory {
  const dir = emptyPeopleDirectory()
  if (!raw || typeof raw !== 'object') return dir
  const o = raw as { groups?: unknown; all?: unknown; teams?: unknown }
  const groups = (o.groups && typeof o.groups === 'object' ? o.groups : {}) as Record<
    string,
    unknown
  >
  const teams = (o.teams && typeof o.teams === 'object' ? o.teams : {}) as Record<string, unknown>
  for (const role of DD_PEOPLE_ROLES) {
    const list = Array.isArray(groups[role]) ? (groups[role] as unknown[]) : []
    dir.groups[role] = list
      .filter(isPerson)
      .map((p) => ({
        uuid: p.uuid,
        name: p.name,
        email: typeof p.email === 'string' ? p.email : ''
      }))
    dir.teams[role] = typeof teams[role] === 'string' ? (teams[role] as string) : null
  }
  const all = Array.isArray(o.all) ? (o.all as unknown[]).filter(isPerson) : []
  dir.all = (all.length ? all : Object.values(dir.groups).flat())
    .map((p) => ({ uuid: p.uuid, name: p.name, email: typeof p.email === 'string' ? p.email : '' }))
    .filter((p, i, arr) => arr.findIndex((q) => q.uuid === p.uuid) === i)
  return dir
}

export function peopleGroups(dir: DdPeopleDirectory): DdPeopleGroup[] {
  return DD_PEOPLE_ROLES.map((key) => ({
    key,
    label: DD_PEOPLE_ROLE_LABELS[key],
    people: dir.groups[key]
  }))
}

export function peopleNames(list: readonly DdPerson[]): string[] {
  return list.map((p) => p.name)
}

/** Pool per role for the Workload board: names only, in directory order. */
export function peoplePools(dir: DdPeopleDirectory): Partial<Record<StepRole, string[]>> {
  const pools: Partial<Record<StepRole, string[]>> = {}
  for (const role of DD_PEOPLE_ROLES) pools[role] = peopleNames(dir.groups[role])
  return pools
}

/**
 * The one person with this display name — null when nobody or several people carry it
 * (then the caller must send the uuid it already has, or fall back to the name).
 */
export function personByName(dir: DdPeopleDirectory, name: string): DdPerson | null {
  const wanted = name.trim().toLowerCase()
  if (!wanted) return null
  const hits = dir.all.filter((p) => p.name.trim().toLowerCase() === wanted)
  return hits.length === 1 ? hits[0]! : null
}

/** Names that occur more than once — the pickers show the email next to those. */
export function duplicateNames(list: readonly DdPerson[]): Set<string> {
  const seen = new Map<string, number>()
  for (const p of list) {
    const k = p.name.trim().toLowerCase()
    seen.set(k, (seen.get(k) ?? 0) + 1)
  }
  return new Set([...seen.entries()].filter(([, n]) => n > 1).map(([k]) => k))
}

/** Uuids for a list of mentioned names (groups like "Sales" and unknown names yield nothing). */
export function mentionUuidsFor(dir: DdPeopleDirectory, mentions: readonly string[]): string[] {
  const out: string[] = []
  for (const name of mentions) {
    const p = personByName(dir, name)
    if (p && !out.includes(p.uuid)) out.push(p.uuid)
  }
  return out
}

// ---------------------------------------------------------------- singleton mirror

let current: DdPeopleDirectory = emptyPeopleDirectory()

/** Called by the queue store whenever the directory changes (and by tests / demos). */
export function setDdPeopleDirectory(dir: DdPeopleDirectory): void {
  current = dir
}

export function ddPeopleDirectory(): DdPeopleDirectory {
  return current
}

export function resetDdPeopleDirectory(): void {
  current = emptyPeopleDirectory()
}
